import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { SortableTh, useSortableRows } from "@/components/SortableTh";
import Shell from "@/components/Shell";
import { formatDateTR } from "@/lib/dateTR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, MagnifyingGlass, ClipboardText, ArrowRight,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  draft:     { label: "Taslak",      cls: "bg-slate-100 text-slate-700 border-slate-300" },
  submitted: { label: "Gönderildi",  cls: "bg-amber-100 text-amber-800 border-amber-300" },
  approved:  { label: "Onaylandı",   cls: "bg-[#F7FBE9] text-[#5c8113] border-[#95C11F]" },
  rejected:  { label: "Reddedildi",  cls: "bg-red-50 text-red-700 border-red-300" },
};

const MONTH_NAMES = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                     "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

export default function ExpenseClaims() {
  const nav = useNavigate();
  const { user } = useAuth();  // eslint-disable-line
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "__all__" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "__all__") params.status = filters.status;
      const { data } = await axios.get(`${API}/expense-claims`, { params });
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status]);

  const filtered = filters.q
    ? rows.filter(r =>
        (r.code || "").toLowerCase().includes(filters.q.toLowerCase()) ||
        (r.user_name || "").toLowerCase().includes(filters.q.toLowerCase()) ||
        (r.site_name || "").toLowerCase().includes(filters.q.toLowerCase())
      )
    : rows;
  const claimsSort = useSortableRows(filtered, {
    defaultKey: "date",
    defaultDir: "desc",
    getters: {
      code: (r) => r?.code,
      user: (r) => r?.user_name,
      site: (r) => r?.site_name,
      period: (r) => `${r?.period_year || ""}-${String(r?.period_month || "").padStart(2, "0")}`,
      date: (r) => r?.date || r?.form_date,
      total: (r) => Number(r?.total) || 0,
      lines: (r) => (r?.lines || []).length,
      status: (r) => r?.status,
    },
  });

  return (
    <Shell
      title="Masraf Talep Formları"
      subtitle="Masraf Bildirimi › Talep Formları"
      hideSiteSelector
      actions={
        <Button variant="outline" onClick={() => nav("/masraf/liste")} data-testid="claims-goto-list"
                className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
          <ClipboardText size={14} className="mr-1.5" /> Masraf Listesinden Yeni Form Oluştur
        </Button>
      }
    >
      <Card className="p-3 rounded-none sm:rounded-sm mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ara</Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <Input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
                   data-testid="claims-filter-q" placeholder="Form no / personel / proje…"
                   className="pl-7 rounded-none sm:rounded-sm h-9" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</Label>
          <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tümü</SelectItem>
              <SelectItem value="draft">Taslak</SelectItem>
              <SelectItem value="submitted">Gönderildi</SelectItem>
              <SelectItem value="approved">Onaylandı</SelectItem>
              <SelectItem value="rejected">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <FileText size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-bold text-slate-700 mb-1">Talep formu yok</div>
          <div className="text-xs text-slate-500 mb-4">Masraf Listesinden birden fazla masraf seçip &quot;Talep Formu Oluştur&quot; ile ilk formunuzu açın.</div>
          <Button onClick={() => nav("/masraf/liste")} className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] text-white">
            Masraf Listesine Git <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </Card>
      ) : (
        <Card className="rounded-none sm:rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
              <tr>
                <SortableTh label="Form No" k="code" sort={claimsSort.sort} onSort={claimsSort.toggleSort} className="text-left px-3 py-2" />
                <SortableTh label="Personel" k="user" sort={claimsSort.sort} onSort={claimsSort.toggleSort} className="text-left px-3 py-2" />
                <SortableTh label="Proje" k="site" sort={claimsSort.sort} onSort={claimsSort.toggleSort} className="text-left px-3 py-2" />
                <SortableTh label="Dönem" k="period" sort={claimsSort.sort} onSort={claimsSort.toggleSort} className="text-left px-3 py-2" />
                <SortableTh label="Form Tarihi" k="date" sort={claimsSort.sort} onSort={claimsSort.toggleSort} className="text-left px-3 py-2" />
                <SortableTh label="Toplam" k="total" sort={claimsSort.sort} onSort={claimsSort.toggleSort} align="right" className="text-right px-3 py-2" />
                <SortableTh label="Adet" k="lines" sort={claimsSort.sort} onSort={claimsSort.toggleSort} align="center" className="text-center px-3 py-2" />
                <SortableTh label="Durum" k="status" sort={claimsSort.sort} onSort={claimsSort.toggleSort} align="center" className="text-center px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {claimsSort.sortedRows.map((r, i) => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
                const monthLabel = r.period_month ? `${MONTH_NAMES[r.period_month - 1]} ${r.period_year}` : "-";
                return (
                  <tr key={r.id} onClick={() => nav(`/masraf/talep/${r.id}`)}
                      data-testid={`claim-row-${i}`}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs font-bold text-[#1B4776]">{r.code}</td>
                    <td className="px-3 py-2 text-xs">{r.user_name}</td>
                    <td className="px-3 py-2 text-xs">{r.site_name || <span className="text-slate-400">— Genel —</span>}</td>
                    <td className="px-3 py-2 text-xs font-medium">{monthLabel}</td>
                    <td className="px-3 py-2 font-mono text-xs">{formatDateTR(r.form_date)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#1B4776]">
                      ₺{(r.total_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-500">{r.expense_count}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${badge.cls}`}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </Shell>
  );
}
